import { useMusicControl } from "@hooks/useMusicControl";
import { ScreenWithPlayingInfo, useScreens } from "@hooks/useScreens";
import { cn } from "@utils/index";
import { LucideIcon, Music, Users } from "lucide-react";
import { useState } from "react";
import { FiMusic } from "react-icons/fi";
import { ManageControlDialog } from "./ManageControlDialog";
import { RequestControlDialog } from "./RequestControlDialog";
import { Tooltip } from "./ui/arrowed-tooltip";
import { Button, ButtonProps } from "./ui/button";

interface MusicControlIndicatorProps {
  screen: ScreenWithPlayingInfo;
  locationId?: string;
  playingMusicName?: string;
  className?: string;
  musicIntegrationEnabled?: boolean;
  variant?: ButtonProps["variant"];
  onClick?: () => void;
}

export function MusicControlIndicator({
  screen,
  locationId,
  className,
  playingMusicName,
  musicIntegrationEnabled,
  variant,
  onClick,
}: MusicControlIndicatorProps) {
  const screenId = screen.id;
  const [manageControlOpen, setManageControlOpen] = useState(false);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const { controlInfo, controlInfos, hasControl } = useMusicControl(screenId, locationId);
  const { screens } = useScreens();

  const ownerControlInfo = controlInfos?.find(control => control.is_owner);

  // Find the owner screen by looking at all screens in the location
  const ownerScreen = screens?.find(s =>
    s.location_id === locationId && s.id === ownerControlInfo?.screen_id
  );

  const getStatusInfo = (): { icon?: LucideIcon; label: React.ReactNode; className: string; showRequestButton: boolean; showMusicName: boolean } => {
    if (!screen.music_integration_enabled || !screen.group_id || !controlInfo) {
      return {
        label: "",
        className: "",
        showRequestButton: false,
        showMusicName: true,
      }
    }

    if (controlInfo?.is_owner) {
      return {
        icon: Users,
        label: "Master",
        className: "text-green-500 p-2 border-l",
        showRequestButton: false,
        showMusicName: true,
      };
    }

    if (controlInfo?.request_status === "pending") {
      return {
        icon: Music,
        label: "Request Pending",
        className: "text-yellow-500",
        showRequestButton: false,
        showMusicName: false,
      };
    }

    if (ownerScreen || controlInfo?.has_control) {
      return {
        icon: Music,
        label: <Tooltip onClick={!controlInfo.has_control ? undefined : (e) => {
          e.stopPropagation()
          onClick?.();
        }} content={ownerScreen ? `Synced with ${ownerScreen.name}` : undefined} side="bottom">
          Synced
        </Tooltip>,
        className: "text-blue-500 p-2",
        showRequestButton: !controlInfo?.has_control,
        showMusicName: false,
      };
    }

    return {
      label: "",
      className: "",
      showRequestButton: false,
      showMusicName: true,
    };
  };

  const { icon: Icon, label, className: _className, showRequestButton, showMusicName } = getStatusInfo();

  return (
    <div className="flex justify-center items-center flex-row">
      {
        showMusicName &&
        <Button
          size="sm"
          variant={variant}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className="w-fit rounded-md border-0"
        >
          <FiMusic className="pr-2 size-6" />
          {!playingMusicName?.length ? musicIntegrationEnabled ? (
            <>Select music</>
          ) : <></> : (
            <OverflowingText>
              {playingMusicName}
            </OverflowingText>
          )}
        </Button>
      }
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (showRequestButton) return setIsRequestDialogOpen(true);
          if (hasControl) return setManageControlOpen(true);
        }}
        className={cn(
          "flex items-center gap-1.5 text-xs",
          className,
          _className,
          showRequestButton && "cursor-pointer hover:text-blue-500 transition-colors"
        )}
      >
        {
          Icon &&
          <Icon className="h-3.5 w-3.5" />
        }
        <span className="hidden sm:inline">{label}</span>
      </button>

      {
        locationId &&
        <>
          <ManageControlDialog
            screenId={screenId}
            locationId={locationId}
            open={manageControlOpen}
            onOpenChange={setManageControlOpen}
          />
          <RequestControlDialog
            screenId={screenId}
            locationId={locationId}
            open={isRequestDialogOpen}
            onOpenChange={setIsRequestDialogOpen}
          />
        </>
      }
    </div>
  );
}

function OverflowingText({ children, className, ...props }: React.ComponentPropsWithoutRef<"div"> & { children: string }) {
  return <div className={cn("max-w-36", className, "overflow-hidden")}>
    <h1 className={cn("text-sm max-w-36", className, "whitespace-nowrap", /*"animate-marquee"*/)} {...props}>
      {children}
    </h1>
  </div>
}